import {ProductDetailsSection_ArticleDetail_SizeLane} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/product_details.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackSizeChartCTA} from '@aboutyou/tracking';
import {BulletPointList, Button, Text} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useProductIdFromSlug} from '../../../../features/ProductUrl';
import {SizeChartComponentProps, SizeCharts} from '../../SizeCharts/SizeCharts';
import {useSizeChartsOpenedState} from '../../SizeCharts/useSizeChartsOpenedState';
import {useSizeAdvisorOpenedState} from '../../utils';
import {SectionWrapper} from './SectionWrapper';

export const SizeSection: React.FC<
  {
    label: string;
    bulletPoints: string[];
    displaySizeAdviser: boolean;
    additionalText?: string;
    sizeCharts: ProductDetailsSection_ArticleDetail_SizeLane['sizeCharts'];
  } & SizeChartComponentProps
> = ({
  label,
  bulletPoints,
  displaySizeAdviser = false,
  additionalText,
  sizeCharts,
  SizeChartLayout,
  SizeSystemLayout,
}) => {
  const productId = useProductIdFromSlug();
  const {t} = useTranslation();
  const {open: openSizeAdvisor} = useSizeAdvisorOpenedState();
  const {open: openSizeCharts} = useSizeChartsOpenedState();

  return (
    <SectionWrapper dataTestId={`productDetailsSizeSection_${label}`}>
      <StyledDescriptionDetails>
        {bulletPoints.length > 0 && (
          <StyledBulletPointList>{bulletPoints}</StyledBulletPointList>
        )}
        {additionalText && <StyledText>{additionalText}</StyledText>}
        <SizeButtonsWrapper>
          {displaySizeAdviser && (
            <Button
              data-testid="openSizeAdvisorButton"
              variant="secondary"
              onClick={() => openSizeAdvisor()}
              size="fit-width"
            >
              {t('Size Finder')}
            </Button>
          )}
          {/* TODO: move to BE the logic to not show the size table when vendor sizes are not available https://aboutyou.atlassian.net/browse/ASFE-6528 */}
          {sizeCharts && sizeCharts.productSizeTable.vendorSizes.length > 0 && (
            <>
              <TrackSizeChartCTA productId={productId!}>
                <Button
                  onClick={() => openSizeCharts()}
                  variant="secondary"
                  data-testid="openSizeChartsButton"
                >
                  {t('Size Chart')}
                </Button>
              </TrackSizeChartCTA>
              <SizeCharts
                SizeChartLayout={SizeChartLayout}
                SizeSystemLayout={SizeSystemLayout}
                introductionText={sizeCharts.introductionText}
                productSizeTable={sizeCharts.productSizeTable}
                bodyMeasurementsTable={sizeCharts.bodyMeasurementsTable}
                measurementHints={sizeCharts.measurementHints}
              />
            </>
          )}
        </SizeButtonsWrapper>
      </StyledDescriptionDetails>
    </SectionWrapper>
  );
};

const StyledDescriptionDetails = styled.dd`
  margin-inline-start: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const StyledBulletPointList = styled(BulletPointList)`
  &&& {
    margin-bottom: 0;
  }
`;

const SizeButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: 1px;
`;

const StyledText = styled(Text)`
  display: inline-block;
`;
